
import React from 'react';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { RefreshCcw, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const AdminUsersList: React.FC = () => {
  const { adminUsers, isLoading, error, fetchAdminUsers } = useAdminUsers();

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-xl">Admin Users</CardTitle>
          <CardDescription>View all administrators with access to this system</CardDescription>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => fetchAdminUsers()} 
          disabled={isLoading}
          className="flex items-center gap-1"
        >
          <RefreshCcw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{error}</p>
              <p className="text-sm text-muted-foreground">
                Note: Viewing admin users requires special permissions in Supabase. 
                This is a limitation of the current implementation.
              </p>
            </AlertDescription>
          </Alert>
        )}
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <p className="text-muted-foreground">Loading admin users...</p>
          </div>
        ) : adminUsers.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-muted-foreground">No admin users found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Note: Viewing all admin users requires Supabase Service Role permissions.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminUsers.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell className="font-medium">{admin.name}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      {admin.created_at ? format(new Date(admin.created_at), 'MMM d, yyyy') : 'Unknown'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUsersList;
